import { WardenHardRule } from './payments'
import { WorkerShiftWithWorkerDetails } from './worker-shift'

export enum AnalyticsActor {
  OPS = 'OPS',
  BUSINESS = 'BUSINESS',
}

export type AnalyticsTrackingFunction = (
  eventName: string,
  // eslint-disable-next-line
  eventParams: Record<string, any>,
) => void
/**
 * Base interface for all analytics events.
 *
 * @interface BaseAnalyticsEvent
 * @property {Record<string, string>} ids - The ids of the event. What primary and foreign keys are connected to this event?
 * @property {Record<string, any>} properties - The properties of the event. Must be in snake_case.
 */
export interface BaseAnalyticsEvent {
  ids: Record<string, string>
  // eslint-disable-next-line
  properties: Record<string, any>
}

export const BUSINESS_CLOCKED_IN_WORKERS =
  'Business Clocked In Workers' as const
export interface BusinessClockedInWorkers extends BaseAnalyticsEvent {
  ids: {
    workerId: string
    shiftId: string
  }
  properties: {
    inline: boolean
    workerShift: Array<WorkerShiftWithWorkerDetails>
    clockInTime: Date
  }
}

export const OPS_USER_DUPLICATED_SHIFT = 'Ops User Duplicated Shift' as const
export interface OpsUserDuplicatedShift extends BaseAnalyticsEvent {
  ids: {
    shiftId: string
  }
  properties: {
    initiatedBy: string
  }
}

export const OPS_USER_CANCELED_ANNOUNCEMENT =
  'Ops User Canceled An Announcement' as const
export interface OpsUserCanceledAnnouncement extends BaseAnalyticsEvent {
  ids: {
    announcementId: string
    shiftId: string
  }
  properties: Record<string, never>
}

export const USER_CANCELED_ANNOUNCEMENT =
  'User Canceled An Announcement' as const
export interface UserCanceledAnnouncement extends BaseAnalyticsEvent {
  ids: {
    announcementId: string
    shiftId: string
  }
  properties: Record<string, never>
}

export const SHIFT_TAG_RECOMMENDATION_DISMISSED =
  'Shift Tag Recommendation Dismissed' as const
export interface ShiftTagRecommendationDismissed extends BaseAnalyticsEvent {
  ids: {
    companyId: string
  }
  properties: {
    roleId: string
    roleName: string
    warningReasons: string[]
    reason: string
    initiatedBy: string
  }
}

export const USER_UPDATED_SLOTS_REQUESTED =
  'User Updated Slots Requested' as const
export interface UserUpdatedSlotsRequested extends BaseAnalyticsEvent {
  ids: {
    userId?: string
    companyId?: string
  }
  properties: {
    slotsRequested: number
    isEdit?: boolean
  }
}

export const SHIFT_REVIEW_SURFACE_CLICKED =
  'Shift Review Surface Clicked' as const

// Worker-list surfaces report how many workers are reviewable / unreviewed.
export interface ShiftReviewSurfaceClickedWithCounts extends BaseAnalyticsEvent {
  ids: {
    shiftId: string
  }
  properties: {
    surface: 'dashboard_block' | 'calendar_pill' | 'shift_detail'
    reviewableWorkerCount: number
    unreviewedWorkerCount: number
  }
}

// Shift-card surfaces are shift-level; per-worker counts don't apply.
export interface ShiftReviewSurfaceClickedShiftCard extends BaseAnalyticsEvent {
  ids: {
    shiftId: string
  }
  properties: {
    surface: 'shift_detail_card_rate' | 'shift_detail_card_edit'
  }
}

// Discriminated union on `surface` so each call site is enforced to pass
// the right shape: worker-list surfaces must include the counts, shift-card
// surfaces must omit them.
export type ShiftReviewSurfaceClicked =
  | ShiftReviewSurfaceClickedWithCounts
  | ShiftReviewSurfaceClickedShiftCard

export const SHIFT_FEEDBACK_SUBMITTED = 'Shift Feedback Submitted' as const
export interface ShiftFeedbackSubmitted extends BaseAnalyticsEvent {
  ids: {
    shiftId: string
    companyId: string
  }
  properties: {
    rating: number | null
    hasNote: boolean
    attributeCount: number
    isUpdate: boolean
    // Where the user submitted from. Distinct from the row's persisted
    // `source` (Company/Ops actor) — see SHIFT_REVIEW_SURFACE_CLICKED.
    surface: 'wizard' | 'shift_detail_card'
  }
}

export const RECENT_SHIFTS_BLOCK_RENDERED =
  'Recent Shifts Block Rendered' as const
export interface RecentShiftsBlockRendered extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: {
    shiftCount: number
  }
}

export const USER_UPDATED_UNITS_REQUESTED =
  'User Updated Units Requested' as const
export interface UserUpdatedUnitsRequested extends BaseAnalyticsEvent {
  ids: {
    userId?: string
    companyId?: string
  }
  properties: {
    unitsRequested?: number
    isEdit?: boolean
  }
}

export const BUGLE_INVITATIONS_SENT = 'Bugle Invitations Sent' as const
export interface BugleInvitationsSent extends BaseAnalyticsEvent {
  ids: {
    shift_id?: string
  }
  properties: {
    workerIds: string[]
    worker_ids: string[]
    shiftId?: string
    actor_id?: string
  }
}

export const WORKER_VETTING_SESSION_PAGE_LOADED =
  'Worker Vetting Session Page Loaded' as const
export interface WorkerVettingSessionPageLoaded extends BaseAnalyticsEvent {
  ids: {
    workerVettingSessionId: string
    workerVettingCampaignId: string
  }
}

export const DOWNLOADED_WORKER_CSV = 'Downloaded Worker CSV' as const

export const DOWNLOADED_TODAYS_WORKERS_CSV =
  'Downloaded Todays Workers CSV' as const

export const ATS_NOTIFY_WORKERS = 'ATS Notify Workers' as const
export interface AtsNotifyWorkers extends BaseAnalyticsEvent {
  ids: {
    shiftId: string
  }
  properties: {
    workerIds: string[]
    workerApplicationIds: string[]
    channel: string
    message: string
    messageLength: number
    deepLinkIncluded: boolean
    skipRateLimit: boolean
    successCount: number
    errorCount: number
    skippedCount: number
    totalWorkers: number
    rateLimitedCount: number
    workerNames: string[]
  }
}

export const AVA_DRAWER_OPENED = 'AVA Drawer Opened' as const
export interface AvaDrawerOpened extends BaseAnalyticsEvent {
  ids: {
    shiftId?: string
  }
  properties: {
    has_shift_id: boolean
    shift_id?: string
    fab_color: string
    fab_shape: string
    fab_size: number
  }
}

export const USER_DOWNLOADED_DOCUMENT = 'User Downloaded Document' as const
export interface UserDownloadedDocument extends BaseAnalyticsEvent {
  ids: {
    workerId: string
    documentId: string
  }
  properties: {
    document: string
  }
}

export const AVA_SPOTLIGHT_OPENED = 'AVA Spotlight Opened' as const

export const OPS_DARK_MODE_TOGGLED = 'Ops Dark Mode Toggled' as const
export interface OpsDarkModeToggled extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: {
    /** The mode the user switched to. */
    mode: 'light' | 'dark'
  }
}

export const OCC_PIPELINE_FUNNEL_ASK_AVA =
  'OCC Pipeline Funnel Ask AVA Clicked' as const
export interface OccPipelineFunnelAskAva extends BaseAnalyticsEvent {
  ids: {
    scheduleId?: string
  }
  properties: {
    has_schedule_id: boolean
    schedule_id?: string
    schedule_name?: string
    /** Metric selected in the toolbar when the user asked AVA. */
    metric: 'p50' | 'p75' | 'p90' | 'mean'
    /** Cohort selected in the toolbar when the user asked AVA. */
    cohort: 'completed' | 'all'
  }
}

export const OCC_PIPELINE_ACTION_DISPATCHED =
  'OCC Pipeline Action Dispatched' as const
export interface OccPipelineActionDispatched extends BaseAnalyticsEvent {
  ids: {
    workerId?: string
    scheduleId?: string
  }
  properties: {
    action_type: string
    has_user_input: boolean
    requirement_id?: string
    worker_requirement_id?: string
  }
}

export const AVA_SEARCH_EXECUTED = 'AVA Search Executed' as const
export interface AvaSearchExecuted extends BaseAnalyticsEvent {
  ids: {
    shiftId?: string
  }
  properties: {
    search_query: string
    query_length: number
    has_shift_id: boolean
    shift_id?: string
  }
}

export const AVA_SEARCH_RESULT_CLICKED = 'AVA Search Result Clicked' as const
export interface AvaSearchResultClicked extends BaseAnalyticsEvent {
  ids: {
    entityId: string
    shiftId?: string
  }
  properties: {
    entity_type: 'worker' | 'shift' | 'company' | 'schedule'
    entity_id: string
    search_query: string
    result_position: number
    tab: string
    has_shift_id: boolean
    shift_id?: string
    destination?: 'schedule_details' | 'ops_control_center'
  }
}

export const AVA_BUGLE_OPENED = 'AVA Bugle Opened' as const
export interface AvaBugleOpened extends BaseAnalyticsEvent {
  ids: {
    shiftId?: string
  }
  properties: {
    has_shift_id: boolean
    shift_id?: string
  }
}

export const AVA_SHIFT_AUTO_FILL_OPENED = 'AVA Shift Auto Fill Opened' as const
export interface AvaShiftAutoFillOpened extends BaseAnalyticsEvent {
  ids: {
    shiftId?: string
  }
  properties: {
    has_shift_id: boolean
    shift_id?: string
  }
}

export const AVA_MESSAGE_SENT = 'AVA Message Sent' as const
export interface AvaMessageSent extends BaseAnalyticsEvent {
  ids: {
    sessionId?: string
    shiftId?: string
  }
  properties: {
    message_length: number
    has_slash_command: boolean
    slash_command?: string
    screen_name?: string
    has_selection_text: boolean
    has_shift_id: boolean
    session_is_new: boolean
    has_attachments?: boolean
    attachment_count?: number
  }
}

export const AVA_RESPONSE_COMPLETED = 'AVA Response Completed' as const
export interface AvaResponseCompleted extends BaseAnalyticsEvent {
  ids: {
    sessionId?: string
  }
  properties: {
    response_time_ms: number
    tools_used: string[]
    has_charts: boolean
    chart_count: number
    response_length: number
  }
}

export const AVA_RESPONSE_ERROR = 'AVA Response Error' as const
export interface AvaResponseError extends BaseAnalyticsEvent {
  ids: {
    sessionId?: string
  }
  properties: {
    error_type: 'timeout' | 'stream_error' | 'api_error'
    error_message: string
    response_time_ms: number
  }
}

export const AVA_STREAMING_CANCELLED = 'AVA Streaming Cancelled' as const
export interface AvaStreamingCancelled extends BaseAnalyticsEvent {
  ids: {
    sessionId?: string
  }
  properties: {
    response_time_ms: number
  }
}

export const AVA_SESSION_STARTED = 'AVA Session Started' as const
export interface AvaSessionStarted extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
  }
  properties: {
    has_shift_id: boolean
    screen_name?: string
  }
}

export const AVA_SESSION_LOADED = 'AVA Session Loaded' as const
export interface AvaSessionLoaded extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
  }
  properties: {
    message_count: number
  }
}

export const AVA_SESSION_ARCHIVED = 'AVA Session Archived' as const
export interface AvaSessionArchived extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
  }
  properties: Record<string, never>
}

export const AVA_RECOMMENDATION_CLICKED = 'AVA Recommendation Clicked' as const
export interface AvaRecommendationClicked extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: {
    recommendation_text: string
    recommendation_index: number
  }
}

export const AVA_HISTORY_TOGGLED = 'AVA History Toggled' as const
export interface AvaHistoryToggled extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: {
    action: 'opened' | 'closed'
  }
}

export const AVA_ASK_AVA_USED = 'AVA Ask Ava Used' as const
/** Which selection modality opened the Ask-Ava chat. */
export type AvaAskAvaSource = 'drag_region' | 'element_pick' | 'text_selection'
export interface AvaAskAvaUsed extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: {
    selection_length: number
    has_custom_question: boolean
    source?: AvaAskAvaSource
  }
}

export const AVA_DARK_MODE_TOGGLED = 'AVA Dark Mode Toggled' as const
export interface AvaDarkModeToggled extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: {
    enabled: boolean
  }
}

export const AVA_CHAT_DOWNLOADED = 'AVA Chat Downloaded' as const
export interface AvaChatDownloaded extends BaseAnalyticsEvent {
  ids: {
    sessionId?: string
  }
  properties: {
    message_count: number
    has_charts: boolean
    trigger: 'header_button' | 'slash_command'
  }
}

export const AVA_FEEDBACK_SUBMITTED = 'AVA Feedback Submitted' as const
export interface AvaFeedbackSubmitted extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
    messageId: string
  }
  properties: {
    rating: 'POSITIVE' | 'NEGATIVE'
    category?: string
    has_comment: boolean
  }
}

export const AVA_FEEDBACK_REMOVED = 'AVA Feedback Removed' as const
export interface AvaFeedbackRemoved extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
    messageId: string
  }
  properties: {
    previous_rating: 'POSITIVE' | 'NEGATIVE'
  }
}

// ---------------------------------------------------------------------------
// Neo (Business App AI Chat) Events
// ---------------------------------------------------------------------------

export const NEO_DRAWER_OPENED = 'Neo Drawer Opened' as const
export interface NeoDrawerOpened extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: {
    source?: 'dashboards' | 'agents'
  }
}

export const NEO_DRAWER_CLOSED = 'Neo Drawer Closed' as const
export interface NeoDrawerClosed extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: {
    source?: 'dashboards' | 'agents'
  }
}

export const NEO_MESSAGE_SENT = 'Neo Message Sent' as const
export interface NeoMessageSent extends BaseAnalyticsEvent {
  ids: {
    sessionId?: string
  }
  properties: {
    message_length: number
    session_is_new: boolean
    has_attachments: boolean
    attachment_count: number
  }
}

export const NEO_RESPONSE_COMPLETED = 'Neo Response Completed' as const
export interface NeoResponseCompleted extends BaseAnalyticsEvent {
  ids: {
    sessionId?: string
  }
  properties: {
    response_time_ms: number
    tools_used: string[]
    has_charts: boolean
    chart_count: number
    response_length: number
  }
}

export const NEO_RESPONSE_ERROR = 'Neo Response Error' as const
export interface NeoResponseError extends BaseAnalyticsEvent {
  ids: {
    sessionId?: string
  }
  properties: {
    error_type: 'timeout' | 'stream_error' | 'api_error'
    error_message: string
    response_time_ms: number
  }
}

export const NEO_STREAMING_CANCELLED = 'Neo Streaming Cancelled' as const
export interface NeoStreamingCancelled extends BaseAnalyticsEvent {
  ids: {
    sessionId?: string
  }
  properties: {
    response_time_ms: number
  }
}

export const NEO_SESSION_STARTED = 'Neo Session Started' as const
export interface NeoSessionStarted extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
  }
  properties: Record<string, never>
}

export const NEO_SESSION_LOADED = 'Neo Session Loaded' as const
export interface NeoSessionLoaded extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
  }
  properties: {
    message_count: number
  }
}

export const NEO_SESSION_ARCHIVED = 'Neo Session Archived' as const
export interface NeoSessionArchived extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
  }
  properties: Record<string, never>
}

export const NEO_HISTORY_TOGGLED = 'Neo History Toggled' as const
export interface NeoHistoryToggled extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: {
    action: 'opened' | 'closed'
  }
}

export const NEO_FEEDBACK_SUBMITTED = 'Neo Feedback Submitted' as const
export interface NeoFeedbackSubmitted extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
    messageId: string
  }
  properties: {
    rating: 'POSITIVE' | 'NEGATIVE'
    category?: string
    has_comment: boolean
  }
}

export const NEO_FEEDBACK_REMOVED = 'Neo Feedback Removed' as const
export interface NeoFeedbackRemoved extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
    messageId: string
  }
  properties: {
    previous_rating: 'POSITIVE' | 'NEGATIVE'
  }
}

export const NEO_RECOMMENDATION_CLICKED = 'Neo Recommendation Clicked' as const
export interface NeoRecommendationClicked extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: {
    recommendation_text: string
    recommendation_index: number
  }
}

export enum InsightRecommendationActionType {
  MARK_DONE = 'mark_done',
  SNOOZE = 'snooze',
  DISMISS = 'dismiss',
  REOPEN = 'reopen',
  HAND_OFF = 'hand_off',
}

export enum InsightHandoffDestination {
  NEUTRON = 'neutron',
  CLAUDE = 'claude',
  CLAUDE_CODE = 'claude_code',
  CHATGPT = 'chatgpt',
  CURSOR = 'cursor',
  COPY = 'copy',
}

export enum InsightCardActionType {
  MARK_ALL_DONE = 'mark_all_done',
  SNOOZE_ALL = 'snooze_all',
  DISMISS_ALL = 'dismiss_all',
  RESET_ALL = 'reset_all',
}

export enum InsightDiscussScope {
  CARD = 'card',
  RECOMMENDATION = 'recommendation',
}

// Triage action taken on a single insight recommendation (the per-action
// Mark done / Snooze / Dismiss / Copy to Claude Code buttons).
export const INSIGHT_RECOMMENDATION_ACTION =
  'Insight Recommendation Action' as const
export interface InsightRecommendationAction extends BaseAnalyticsEvent {
  ids: {
    insightId: string
  }
  properties: {
    action: InsightRecommendationActionType
    recommendation_id: string
    recommendation_title: string
    // Dismiss only: the free-text reason the operator gave (if any).
    reason?: string
    // Snooze only: ISO timestamp the rec is snoozed until.
    snooze_until?: string
    // hand_off only: where the operator sent the recommendation prompt.
    handoff_destination?: InsightHandoffDestination
  }
}

// Whole-card cascade action (Mark all done / Snooze all / Dismiss all),
// applied to every recommendation on the card at once.
export const INSIGHT_CARD_ACTION = 'Insight Card Action' as const
export interface InsightCardAction extends BaseAnalyticsEvent {
  ids: {
    insightId: string
  }
  properties: {
    action: InsightCardActionType
    // Snooze only: ISO timestamp the card is snoozed until.
    snooze_until?: string
  }
}

// "Discuss with Neutron" opened from an insight — for the whole card or a
// specific recommendation.
export const INSIGHT_DISCUSS_CLICKED = 'Insight Discuss Clicked' as const
export interface InsightDiscussClicked extends BaseAnalyticsEvent {
  ids: {
    insightId: string
  }
  properties: {
    scope: InsightDiscussScope
    recommendation_id?: string
    recommendation_title?: string
  }
}

export const NEO_CHAT_DOWNLOADED = 'Neo Chat Downloaded' as const
export interface NeoChatDownloaded extends BaseAnalyticsEvent {
  ids: {
    sessionId?: string
  }
  properties: {
    message_count: number
    has_charts: boolean
    trigger: 'header_button' | 'slash_command'
  }
}

export const NEO_SESSION_SELECTED = 'Neo Session Selected' as const
export interface NeoSessionSelected extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
  }
  properties: Record<string, never>
}

export const NEO_SESSION_PIN_TOGGLED = 'Neo Session Pin Toggled' as const
export interface NeoSessionPinToggled extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
  }
  properties: {
    pinned: boolean
  }
}

export const NEO_APPROVAL_SUBMITTED = 'Neo Approval Submitted' as const
export interface NeoApprovalSubmitted extends BaseAnalyticsEvent {
  ids: {
    proposalId: string
  }
  properties: {
    approved: boolean
    action_type: string
  }
}

// ---------------------------------------------------------------------------
// Neo Platform Artifact Events
// ---------------------------------------------------------------------------

export const NEO_ARTIFACT_VIEWED = 'Neo Artifact Viewed' as const
export interface NeoArtifactViewed extends BaseAnalyticsEvent {
  ids: {
    artifactId: string
  }
  properties: {
    source: 'detail_page' | 'inline_card'
    block_count: number
  }
}

export const NEO_ARTIFACT_FAVORITE_TOGGLED =
  'Neo Artifact Favorite Toggled' as const
export interface NeoArtifactFavoriteToggled extends BaseAnalyticsEvent {
  ids: {
    artifactId: string
  }
  properties: {
    favorited: boolean
    source: 'detail_page' | 'inline_card'
  }
}

export const NEO_ARTIFACT_ARCHIVED = 'Neo Artifact Archived' as const
export interface NeoArtifactArchived extends BaseAnalyticsEvent {
  ids: {
    artifactId: string
  }
  properties: {
    source: 'detail_page' | 'inline_card'
  }
}

export const NEO_ARTIFACT_UNARCHIVED = 'Neo Artifact Unarchived' as const
export interface NeoArtifactUnarchived extends BaseAnalyticsEvent {
  ids: {
    artifactId: string
  }
  properties: Record<string, never>
}

export const NEO_ARTIFACT_CLONED = 'Neo Artifact Cloned' as const
export interface NeoArtifactCloned extends BaseAnalyticsEvent {
  ids: {
    artifactId: string
  }
  properties: Record<string, never>
}

export const NEO_ARTIFACT_EDITED = 'Neo Artifact Edited' as const
export interface NeoArtifactEdited extends BaseAnalyticsEvent {
  ids: {
    artifactId: string
  }
  properties: {
    field: 'title' | 'summary'
  }
}

// ---------------------------------------------------------------------------
// Neo Platform Dashboard Events
// ---------------------------------------------------------------------------

export const NEO_DASHBOARD_VIEWED = 'Neo Dashboard Viewed' as const
export interface NeoDashboardViewed extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: {
    panel_count: number
    is_owner: boolean
  }
}

export const NEO_DASHBOARD_EDITED = 'Neo Dashboard Edited' as const
export interface NeoDashboardEdited extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: {
    change_count: number
    change_types: string[]
  }
}

export const NEO_DASHBOARD_EDITED_WITH_AI =
  'Neo Dashboard Edited With AI' as const
export interface NeoDashboardEditedWithAi extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: {
    target: 'dashboard' | 'panel' | 'description'
  }
}

export const NEO_DASHBOARD_CLONED = 'Neo Dashboard Cloned' as const
export interface NeoDashboardCloned extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: Record<string, never>
}

export const NEO_DASHBOARD_DELETED = 'Neo Dashboard Deleted' as const
export interface NeoDashboardDeleted extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: Record<string, never>
}

export const NEO_DASHBOARD_FAVORITE_TOGGLED =
  'Neo Dashboard Favorite Toggled' as const
export interface NeoDashboardFavoriteToggled extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: {
    favorited: boolean
  }
}

export const NEO_DASHBOARD_ARCHIVED = 'Neo Dashboard Archived' as const
export interface NeoDashboardArchived extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: Record<string, never>
}

export const NEO_DASHBOARD_UNARCHIVED = 'Neo Dashboard Unarchived' as const
export interface NeoDashboardUnarchived extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: Record<string, never>
}

export const NEO_DASHBOARD_VERSION_PROMOTED =
  'Neo Dashboard Version Promoted' as const
export interface NeoDashboardVersionPromoted extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: {
    promoted_version: number
  }
}

export const NEO_DASHBOARDS_LIST_FILTERED =
  'Neo Dashboards List Filtered' as const
export interface NeoDashboardsListFiltered extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: {
    control: 'status' | 'owner' | 'view_mode'
    value: string
  }
}

export const NEO_DASHBOARDS_SEARCHED = 'Neo Dashboards Searched' as const
export interface NeoDashboardsSearched extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: Record<string, never>
}

export const NEO_DASHBOARD_VERSION_HISTORY_OPENED =
  'Neo Dashboard Version History Opened' as const
export interface NeoDashboardVersionHistoryOpened extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: Record<string, never>
}

export const NEO_DASHBOARD_VERSION_PREVIEWED =
  'Neo Dashboard Version Previewed' as const
export interface NeoDashboardVersionPreviewed extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: {
    version: number
  }
}

export const NEO_DASHBOARD_EDIT_MODE_ENTERED =
  'Neo Dashboard Edit Mode Entered' as const
export interface NeoDashboardEditModeEntered extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: Record<string, never>
}

// ---------------------------------------------------------------------------
// Neo Platform Agent Events
// ---------------------------------------------------------------------------

export const NEO_AGENT_VIEWED = 'Neo Agent Viewed' as const
export interface NeoAgentViewed extends BaseAnalyticsEvent {
  ids: {
    agentId: string
  }
  properties: {
    status: string
  }
}

export const NEO_AGENTS_LIST_FILTERED = 'Neo Agents List Filtered' as const
export interface NeoAgentsListFiltered extends BaseAnalyticsEvent {
  ids: Record<string, never>
  properties: {
    show_archived: boolean
    filter?: string
  }
}

export const NEO_AGENT_PAUSED = 'Neo Agent Paused' as const
export interface NeoAgentPaused extends BaseAnalyticsEvent {
  ids: {
    agentId: string
  }
  properties: {
    source: 'list' | 'detail'
  }
}

export const NEO_AGENT_RESUMED = 'Neo Agent Resumed' as const
export interface NeoAgentResumed extends BaseAnalyticsEvent {
  ids: {
    agentId: string
  }
  properties: {
    source: 'list' | 'detail'
  }
}

export const NEO_AGENT_CANCELLED = 'Neo Agent Cancelled' as const
export interface NeoAgentCancelled extends BaseAnalyticsEvent {
  ids: {
    agentId: string
  }
  properties: Record<string, never>
}

export const NEO_AGENT_ARCHIVED = 'Neo Agent Archived' as const
export interface NeoAgentArchived extends BaseAnalyticsEvent {
  ids: {
    agentId: string
  }
  properties: Record<string, never>
}

export const NEO_AGENT_UNARCHIVED = 'Neo Agent Unarchived' as const
export interface NeoAgentUnarchived extends BaseAnalyticsEvent {
  ids: {
    agentId: string
  }
  properties: Record<string, never>
}

export const NEO_AGENT_RUN_TRIGGERED = 'Neo Agent Run Triggered' as const
export interface NeoAgentRunTriggered extends BaseAnalyticsEvent {
  ids: {
    agentId: string
  }
  properties: Record<string, never>
}

export const NEO_AGENT_RUN_ARCHIVED = 'Neo Agent Run Archived' as const
export interface NeoAgentRunArchived extends BaseAnalyticsEvent {
  ids: {
    agentId: string
    runId: string
  }
  properties: Record<string, never>
}

export const NEO_AGENT_RUN_UNARCHIVED = 'Neo Agent Run Unarchived' as const
export interface NeoAgentRunUnarchived extends BaseAnalyticsEvent {
  ids: {
    agentId: string
    runId: string
  }
  properties: Record<string, never>
}

export const NEO_AGENT_ASK_NEO_CLICKED = 'Neo Agent Ask Neo Clicked' as const
export interface NeoAgentAskNeoClicked extends BaseAnalyticsEvent {
  ids: {
    agentId?: string
  }
  properties: {
    source: 'header' | 'needs_you_card' | 'empty_state_suggestion'
  }
}

export const NEO_AGENT_RUN_HISTORY_TOGGLED =
  'Neo Agent Run History Toggled' as const
export interface NeoAgentRunHistoryToggled extends BaseAnalyticsEvent {
  ids: {
    agentId: string
  }
  properties: {
    action: 'opened' | 'closed'
  }
}

export const NEO_AGENT_ARCHIVED_RUNS_TOGGLED =
  'Neo Agent Archived Runs Toggled' as const
export interface NeoAgentArchivedRunsToggled extends BaseAnalyticsEvent {
  ids: {
    agentId: string
  }
  properties: {
    show_archived: boolean
  }
}

export const OPS_DRUG_SCREENING_SHIFT_CONFIGURED =
  'Ops Drug Screening Shift Configured' as const
export interface OpsDrugScreeningShiftConfigured extends BaseAnalyticsEvent {
  ids: {
    shiftId?: string
    shiftRequestId?: string
    opsUserId?: string
  }
  properties: {
    drug_panel_type?: string
    drug_screening_vendor?: string
    panel_count?: number
    is_required?: boolean
  }
}

export const OPS_DRUG_SCREENING_TEMPLATE_CONFIGURED =
  'Ops Drug Screening Template Configured' as const
export interface OpsDrugScreeningTemplateConfigured extends BaseAnalyticsEvent {
  ids: {
    templateId?: string
    opsUserId?: string
  }
  properties: {
    template_name?: string
    template_entity_type?: 'ROLE' | 'ROLE_FAMILY'
    drug_panel_type?: string
    drug_screening_vendor?: string
    panel_count?: number
    is_required?: boolean
  }
}

export const OPS_DRUG_SCREENING_APPLICATION_CONFIGURED =
  'Ops Drug Screening Application Configured' as const
export interface OpsDrugScreeningApplicationConfigured extends BaseAnalyticsEvent {
  ids: {
    applicationId?: string
    applicationItemId?: string
    shiftId?: string
    shiftRequestId?: string
    opsUserId?: string
  }
  properties: {
    drug_panel_type?: string
    drug_screening_vendor?: string
    auto_reject_on_positive?: boolean
    auto_create_order?: boolean
    deadline_days?: number
    is_update?: boolean
  }
}

export const OPS_DRUG_SCREENING_WORKER_APPROVED =
  'Ops Drug Screening Worker Approved' as const
export interface OpsDrugScreeningWorkerApproved extends BaseAnalyticsEvent {
  ids: {
    workerId?: string
    workerApplicationItemId?: string
    applicationId?: string
    applicationItemId?: string
    shiftId?: string
    orderId?: string
    opsUserId?: string
  }
  properties: {
    approval_reason?: string
  }
}

export const OPS_DRUG_SCREENING_WORKER_WAIVED =
  'Ops Drug Screening Worker Waived' as const
export interface OpsDrugScreeningWorkerWaived extends BaseAnalyticsEvent {
  ids: {
    workerId?: string
    workerApplicationItemId?: string
    applicationId?: string
    applicationItemId?: string
    shiftId?: string
    orderId?: string
    opsUserId?: string
  }
  properties: {
    waiver_reason?: string
  }
}

export const OPS_DRUG_SCREENING_WORKER_REJECTED =
  'Ops Drug Screening Worker Rejected' as const
export interface OpsDrugScreeningWorkerRejected extends BaseAnalyticsEvent {
  ids: {
    workerId?: string
    workerApplicationItemId?: string
    applicationId?: string
    applicationItemId?: string
    shiftId?: string
    orderId?: string
    opsUserId?: string
  }
  properties: {
    rejection_reason?: string
  }
}

export const OPS_DRUG_SCREENING_ORDER_CREATED =
  'Ops Drug Screening Order Created' as const
export interface OpsDrugScreeningOrderCreated extends BaseAnalyticsEvent {
  ids: {
    orderId?: string
    workerId?: string
    shiftRequestId?: string
    opsUserId?: string
  }
  properties: {
    drug_panel_type?: string
    drug_screening_vendor?: string
    expiration_days?: number
    employment_type?: string
  }
}

export const OPS_DRUG_SCREENING_ORDER_CANCELLED =
  'Ops Drug Screening Order Cancelled' as const
export interface OpsDrugScreeningOrderCancelled extends BaseAnalyticsEvent {
  ids: {
    orderId?: string
    workerId?: string
    opsUserId?: string
  }
  properties: {
    cancellation_reason?: string
    order_status?: string
  }
}

export const OPS_DRUG_SCREENING_CONFIGURATION_UPDATED =
  'Ops Drug Screening Configuration Updated' as const
export interface OpsDrugScreeningConfigurationUpdated extends BaseAnalyticsEvent {
  ids: {
    applicationId?: string
    applicationItemId?: string
    shiftId?: string
    opsUserId?: string
  }
  properties: {
    previous_drug_panel_type?: string
    new_drug_panel_type?: string
    previous_drug_screening_vendor?: string
    new_drug_screening_vendor?: string
    previous_panel_count?: number
    new_panel_count?: number
    previous_is_required?: boolean
    new_is_required?: boolean
  }
}

export const OPS_DRUG_SCREENING_REVIEW_SCREEN_VIEWED =
  'Ops Drug Screening Review Screen Viewed' as const
export interface OpsDrugScreeningReviewScreenViewed extends BaseAnalyticsEvent {
  ids: {
    workerId?: string
    workerApplicationItemId?: string
    applicationId?: string
    applicationItemId?: string
    shiftId?: string
    orderId?: string
    opsUserId?: string
  }
  properties: {
    order_status?: string
  }
}

export const OPS_DOCUMENT_SIGNING_TEMPLATE_CONFIGURED =
  'Ops Document Signing Template Configured' as const
export interface OpsDocumentSigningTemplateConfigured extends BaseAnalyticsEvent {
  ids: {
    templateId?: string
    opsUserId?: string
  }
  properties: {
    template_name?: string
    template_entity_type?: 'ROLE' | 'ROLE_FAMILY'
    docusign_template_id?: string
  }
}

export const TRANSFER_WORKERS_COMPLETED = 'Transfer Workers Completed' as const
export interface TransferWorkersCompleted extends BaseAnalyticsEvent {
  ids: {
    sourceShiftRequestId?: string
  }
  properties: {
    worker_ids: string[]
    target_shift_ids: string[]
    worker_to_shifts_map: Record<string, string[]>
    cancelled_shifts_count: number
    source: string
  }
}

export const OKTA_SSO_INITIATED = 'Okta SSO Initiated' as const
export interface OktaSsoInitiated extends BaseAnalyticsEvent {
  ids: {
    providerId: string
  }
  properties: {
    auth_app_name: string
    initiating_url?: string
  }
}

export const OKTA_SSO_SUCCESS = 'Okta SSO Success' as const
export interface OktaSsoSuccess extends BaseAnalyticsEvent {
  ids: {
    userId: string
    providerId: string
  }
  properties: {
    user_email?: string
    user_display_name?: string
    operation_type: string
    provider_data?: string
  }
}

export const OKTA_SSO_FAILED = 'Okta SSO Failed' as const
export interface OktaSsoFailed extends BaseAnalyticsEvent {
  ids: {
    providerId: string
  }
  properties: {
    error_message: string
    error_code: string
    error_name?: string
    error_custom_data?: string
    current_url?: string
    initiating_url?: string
    oauth_error?: string
    oauth_error_description?: string
    oauth_state?: string
    oauth_credential_info?: string
    full_error_debug?: string
    url_hash?: string
    environment_info?: string
    session_storage_debug?: string
  }
}

// ---------------------------------------------------------------------------
// OCC Onsite Tools Events
// ---------------------------------------------------------------------------

export const OPS_ONSITE_TAB_SWITCHED = 'Ops Onsite Tab Switched'
export interface OpsOnsiteTabSwitched extends BaseAnalyticsEvent {
  ids: {
    companyId: string
    scheduleId: string
  }
  properties: {
    from_tab: 'scheduled' | 'pending'
    to_tab: 'scheduled' | 'pending'
  }
}

export const OPS_ONSITE_WALK_IN_INITIATED = 'Ops Onsite Walk In Initiated'
export interface OpsOnsiteWalkInInitiated extends BaseAnalyticsEvent {
  ids: {
    companyId: string
    scheduleId: string
  }
  properties: {
    has_prefilled_phone: boolean
  }
}

export const OPS_ONSITE_PHONE_SEARCH_COMPLETED =
  'Ops Onsite Phone Search Completed'
export interface OpsOnsitePhoneSearchCompleted extends BaseAnalyticsEvent {
  ids: {
    companyId: string
  }
  properties: {
    outcome:
      | 'existing_worker'
      | 'new_ghost'
      | 'blocked_queue'
      | 'blocked_application'
      | 'blocked_error'
    has_worker_match: boolean
  }
}

export const OPS_ONSITE_CANDIDATE_ADDED = 'Ops Onsite Candidate Added'
export interface OpsOnsiteCandidateAdded extends BaseAnalyticsEvent {
  ids: {
    companyId: string
    applicationId: string
    candidateId?: string
  }
  properties: {
    has_score: boolean
    has_notes: boolean
    has_cohort: boolean
    requirement_count: number
    is_linked_worker: boolean
    candidate_kind: 'WORKER' | 'GHOST'
  }
}

export const OPS_ONSITE_CANDIDATE_UPDATED = 'Ops Onsite Candidate Updated'
export interface OpsOnsiteCandidateUpdated extends BaseAnalyticsEvent {
  ids: {
    companyId: string
    candidateId: string
  }
  properties: {
    has_score: boolean
    has_notes: boolean
    has_cohort: boolean
    requirement_count: number
    candidate_status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'DISMISSED'
  }
}

export const OPS_ONSITE_CANDIDATE_DISMISSED = 'Ops Onsite Candidate Dismissed'
export interface OpsOnsiteCandidateDismissed extends BaseAnalyticsEvent {
  ids: {
    companyId: string
  }
  properties: {
    source: 'candidate_page' | 'occ_queue'
    candidate_count: number
  }
}

export const OPS_ONSITE_COHORT_FILTER_TOGGLED =
  'Ops Onsite Cohort Filter Toggled'
export interface OpsOnsiteCohortFilterToggled extends BaseAnalyticsEvent {
  ids: {
    companyId: string
    scheduleId: string
  }
  properties: {
    tag_name: string
    action: 'added' | 'removed'
    active_filter_count: number
  }
}

export const NEUTRON_DASHBOARD_VIEWED = 'Neutron Dashboard Viewed' as const
export interface NeutronDashboardViewed extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: {
    panel_count: number
    is_owner: boolean
  }
}

export const NEUTRON_DASHBOARD_CREATED = 'Neutron Dashboard Created' as const
export interface NeutronDashboardCreated extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
    sessionId?: string
  }
  properties: Record<string, never>
}

export const NEUTRON_DASHBOARD_EDITED = 'Neutron Dashboard Edited' as const
export interface NeutronDashboardEdited extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: {
    change_count: number
    change_types: string[]
  }
}

export const NEUTRON_PANEL_UPDATED = 'Neutron Panel Updated' as const
export interface NeutronPanelUpdated extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: {
    edit_version: number
    affected_panel_count: number
  }
}

export const NEUTRON_DASHBOARD_SHARED = 'Neutron Dashboard Shared' as const
export interface NeutronDashboardShared extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: {
    share_method: 'clipboard' | 'prompt'
  }
}

export const NEUTRON_DASHBOARD_VERSION_PROMOTED =
  'Neutron Dashboard Version Promoted' as const
export interface NeutronDashboardVersionPromoted extends BaseAnalyticsEvent {
  ids: {
    dashboardId: string
  }
  properties: {
    promoted_version: number
  }
}

// ---------------------------------------------------------------------------
// W2 ClearingHouse review-session events (ENG-18587)
//
// Powers the Datadog dashboards described in the W2 ClearingHouse
// Observability Plan: throughput per session, %-bulk vs single, per-action
// outcome distribution, and the false-positive-rate denominator.
//
// All three events share `sessionId` — a client-held UUID minted once per
// `<ClearingHouseW2>` mount — so the events can be joined into a single
// review-session timeline server-side.
// ---------------------------------------------------------------------------

/** Discriminator on `<ClearingHouseW2>` row kind (paycheck card vs shift-flag card). */
export type ClearingHouseRowKind = 'PAYCHECK' | 'SHIFT_FLAG'

/** Mark-as actions surfaced by the bulk-actions toolbar (PR 2.2 / #5668). */
export type ClearingHouseAction =
  | 'DISMISS'
  | 'MARK_ADJUSTED'
  | 'APPROVE'
  | 'UNRESOLVE'

/** Per-kind row-count breakdown for `Clearing House Action Taken`. */
export interface ClearingHouseRowKindBreakdown {
  PAYCHECK: number
  SHIFT_FLAG: number
}

export const CLEARING_HOUSE_SESSION_STARTED =
  'Clearing House Session Started' as const
export interface ClearingHouseSessionStarted extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
  }
  properties: Readonly<{
    /** Email of the ops user who opened the screen — same context as `useAnalytics()`. */
    userEmail: string | undefined
    /** ISO 8601 timestamp at mount; lets dashboards compute time-of-day cohorts. */
    openedAt: string
  }>
}

export const CLEARING_HOUSE_ROW_VIEWED = 'Clearing House Row Viewed' as const
export interface ClearingHouseRowViewed extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
    /** PaycheckId for `PAYCHECK` rows; wardenResultId for `SHIFT_FLAG` rows. */
    rowId: string
  }
  properties: Readonly<{
    rowKind: ClearingHouseRowKind
    /** WardenHardRule strings the row triggered. Empty array when none. */
    flagReasons: WardenHardRule[]
    /** Snapshot of `OpsFeatureFlag.CLEARING_HOUSE_W2_UI_OVERHAUL` at view time. */
    gateOpen: boolean
  }>
}

export const CLEARING_HOUSE_ACTION_TAKEN =
  'Clearing House Action Taken' as const
export interface ClearingHouseActionTaken extends BaseAnalyticsEvent {
  ids: {
    sessionId: string
  }
  properties: Readonly<{
    action: ClearingHouseAction
    /** Total rows the action targeted (1 for single-row mutations). */
    rowCount: number
    /**
     * Derived `rowCount > 1`. Surfaced as a property so Datadog can
     * filter actions by single-vs-bulk via tag equality
     * (`bulk:true` / `bulk:false`) — Datadog metric tag filters do
     * not support numeric comparison operators, so `rowCount` alone
     * isn't queryable for the bulk-adoption dashboard widget.
     */
    bulk: boolean
    rowKindBreakdown: ClearingHouseRowKindBreakdown
    /**
     * Correlation id from the bulk-action server response when the API
     * returns one; otherwise `null`. Reserved for future plumbing — the
     * current PATCH endpoints do not return one.
     */
    correlationId: string | null
    /**
     * Milliseconds elapsed since the first `Clearing House Row Viewed`
     * event in this session. `null` when no row had been viewed yet
     * (action triggered before any row scrolled into view).
     */
    durationMs: number | null
  }>
}

// ---------------------------------------------------------------------------
// Incident Center Events
// ---------------------------------------------------------------------------

export const INCIDENT_VIEW_OPENED = 'Incident View Opened' as const
export interface IncidentViewOpened extends BaseAnalyticsEvent {
  ids: {
    incidentId: string
  }
  properties: {
    report_status: string
  }
}

export const INCIDENT_PHOTO_APPENDED = 'Incident Photo Appended' as const
export interface IncidentPhotoAppended extends BaseAnalyticsEvent {
  ids: {
    incidentId: string
    photoId: string
  }
  properties: Record<string, never>
}

export const INCIDENT_WITNESS_PDF_ATTACHED =
  'Incident Witness PDF Attached' as const
export interface IncidentWitnessPdfAttached extends BaseAnalyticsEvent {
  ids: {
    incidentId: string
    witnessStatementId: string
  }
  properties: Record<string, never>
}

export type AnalyticsEvent =
  | BusinessClockedInWorkers
  | OpsUserDuplicatedShift
  | OpsUserCanceledAnnouncement
  | UserCanceledAnnouncement
  | ShiftTagRecommendationDismissed
  | UserUpdatedSlotsRequested
  | UserUpdatedUnitsRequested
  | ShiftReviewSurfaceClicked
  | ShiftFeedbackSubmitted
  | RecentShiftsBlockRendered
  | BugleInvitationsSent
  | WorkerVettingSessionPageLoaded
  | AtsNotifyWorkers
  | UserDownloadedDocument
  | AvaDrawerOpened
  | OccPipelineFunnelAskAva
  | AvaSearchExecuted
  | AvaSearchResultClicked
  | AvaBugleOpened
  | AvaShiftAutoFillOpened
  | OpsDrugScreeningShiftConfigured
  | OpsDrugScreeningTemplateConfigured
  | OpsDrugScreeningApplicationConfigured
  | OpsDrugScreeningWorkerApproved
  | OpsDrugScreeningWorkerWaived
  | OpsDrugScreeningWorkerRejected
  | OpsDrugScreeningOrderCreated
  | OpsDrugScreeningOrderCancelled
  | OpsDrugScreeningConfigurationUpdated
  | OpsDrugScreeningReviewScreenViewed
  | OpsDocumentSigningTemplateConfigured
  | TransferWorkersCompleted
  | OktaSsoInitiated
  | OktaSsoSuccess
  | OktaSsoFailed
  | AvaMessageSent
  | AvaResponseCompleted
  | AvaResponseError
  | AvaStreamingCancelled
  | AvaSessionStarted
  | AvaSessionLoaded
  | AvaSessionArchived
  | AvaRecommendationClicked
  | AvaHistoryToggled
  | AvaAskAvaUsed
  | AvaDarkModeToggled
  | AvaChatDownloaded
  | AvaFeedbackSubmitted
  | AvaFeedbackRemoved
  | NeoDrawerOpened
  | NeoMessageSent
  | NeoResponseCompleted
  | NeoResponseError
  | NeoStreamingCancelled
  | NeoSessionStarted
  | NeoSessionLoaded
  | NeoSessionArchived
  | NeoHistoryToggled
  | NeoFeedbackSubmitted
  | NeoFeedbackRemoved
  | NeoRecommendationClicked
  | NeoChatDownloaded
  | NeoSessionSelected
  | NeoSessionPinToggled
  | NeoApprovalSubmitted
  | NeoArtifactViewed
  | NeoArtifactFavoriteToggled
  | NeoArtifactArchived
  | NeoArtifactUnarchived
  | NeoArtifactCloned
  | NeoArtifactEdited
  | NeoDashboardViewed
  | NeoDashboardEdited
  | NeoDashboardEditedWithAi
  | NeoDashboardCloned
  | NeoDashboardDeleted
  | NeoDashboardFavoriteToggled
  | NeoDashboardArchived
  | NeoDashboardUnarchived
  | NeoDashboardVersionPromoted
  | NeoDashboardsListFiltered
  | NeoDashboardsSearched
  | NeoDashboardVersionHistoryOpened
  | NeoDashboardVersionPreviewed
  | NeoDashboardEditModeEntered
  | NeoDrawerClosed
  | NeoAgentViewed
  | NeoAgentsListFiltered
  | NeoAgentPaused
  | NeoAgentResumed
  | NeoAgentCancelled
  | NeoAgentArchived
  | NeoAgentUnarchived
  | NeoAgentRunTriggered
  | NeoAgentRunArchived
  | NeoAgentRunUnarchived
  | NeoAgentAskNeoClicked
  | NeoAgentRunHistoryToggled
  | NeoAgentArchivedRunsToggled
  | OpsOnsiteTabSwitched
  | OpsOnsiteWalkInInitiated
  | OpsOnsitePhoneSearchCompleted
  | OpsOnsiteCandidateAdded
  | OpsOnsiteCandidateUpdated
  | OpsOnsiteCandidateDismissed
  | OpsOnsiteCohortFilterToggled
  | NeutronDashboardViewed
  | NeutronDashboardCreated
  | NeutronDashboardEdited
  | NeutronPanelUpdated
  | NeutronDashboardShared
  | NeutronDashboardVersionPromoted
  | ClearingHouseSessionStarted
  | ClearingHouseRowViewed
  | ClearingHouseActionTaken
  | OccPipelineActionDispatched
  | IncidentViewOpened
  | IncidentPhotoAppended
  | IncidentWitnessPdfAttached
  | InsightRecommendationAction
  | InsightCardAction
  | InsightDiscussClicked
